import React from 'react';
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';

const KIND_TR = { AC: '🎯 AÇ', EMIR_KOY: '🔭 EMİR KOY', KUCUK_AC: '🚀 KÜÇÜK AÇ', ETIKET: '🔥 ETİKET', PATLAMA: '💥 PATLAMA', ISINMA: '🔥 ISINIYOR', HABER: '📰 HABER' };

function Line({ ok, warn, children, testId }) {
    const Icon = ok ? CheckCircle2 : warn ? AlertTriangle : Info;
    const cls = ok ? 'text-emerald-300' : warn ? 'text-amber-300' : 'text-slate-400';
    return (
        <div className="flex items-start gap-1.5 text-[11px]" data-testid={testId}>
            <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${cls}`} />
            <span className={ok ? 'text-slate-300' : warn ? 'text-amber-100' : 'text-slate-300'}>{children}</span>
        </div>
    );
}

/** Boş slot neden kullanılmıyor? — teminat, patlama/haber slotu, öğrenme durdurması, günlük limit ve adayların açılmama nedenleri (seçili defter). */
export default function SlotDiagnosis({ view, settings, mode, onFix, busy }) {
    if (!view || !view.enabled) return null;
    const a = view.auto || {};
    const slots = Number(settings.slots || 0);
    const used = (view.open_count || 0) + (view.pending_count || 0);
    const free = Math.max(0, slots - used);
    const paused = view.learning?.paused || [];
    const skips = Object.entries(view.skips || {});
    const grouped = skips.reduce((acc, [sym, sk]) => {
        const key = sk.reason || 'neden belirtilmedi';
        (acc[key] = acc[key] || []).push(sym.replace('USDT', ''));
        return acc;
    }, {});
    const reasons = Object.entries(grouped).sort((x, y) => y[1].length - x[1].length).slice(0, 6);
    const marginOk = a.enough !== false;
    const limitHit = (view.used_pct || 0) >= 100 || (view.block || '').includes('Günlük zarar');
    // bakiyeye göre öneri: hem şimdi boş slota sığsın hem de tüm slotlar bakiyeye bölünsün
    const suggest = !settings.auto_size && free > 0 && a.equity != null && a.available != null
        ? Math.max(20, Math.min(Math.floor(Number(a.equity) / slots), Math.floor((Number(a.available) - Number(a.reserved_usd || 0)) * 0.99 / free))) : null;
    return (
        <div className="rounded-lg border border-slate-700/70 bg-slate-900/60 p-3 space-y-1.5" data-testid="autotrade-slot-diagnosis">
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-bold text-slate-50">Slot durumu · {mode === 'real' ? '🏦 gerçek' : '🧪 sanal'}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${free > 0 ? 'bg-emerald-500/15 text-emerald-200' : 'bg-slate-700 text-slate-200'}`} data-testid="autotrade-slot-count">
                    {view.open_count || 0} açık + {view.pending_count || 0} bekleyen / {slots} → {free} boş
                </span>
                {view.block && <span className="text-[10px] text-amber-200" data-testid="autotrade-slot-block">{view.block}</span>}
            </div>
            {free > 0 && (
                <div className="space-y-1">
                    {a.available != null && (
                        <Line ok={marginOk} warn={!marginOk} testId="autotrade-slot-margin">
                            Teminat: kullanılabilir <b>{Number(a.available).toFixed(0)}$</b>{a.reserved_usd > 0 ? ` (bekleyen emirlere ayrılan ${Number(a.reserved_usd).toFixed(0)}$ düşüldü)` : ''} · yeni slot için{' '}
                            <b>{Number(a.required_usd || 0).toFixed(0)}$</b> gerekir
                            {settings.auto_size ? ' (otomatik: en az 20$)' : ' (sabit slot başı teminat)'} →{' '}
                            {marginOk ? `yetiyor${a.fits_now != null ? ` (${Math.min(free, a.fits_now)} slota)` : ''}` : 'YETMİYOR — bot küçültmez, bekler. Bakiye ekle ya da slot başı teminatı düşür'}
                        </Line>
                    )}
                    {!marginOk && suggest != null && suggest >= 20 && onFix && (
                        <div className="pl-5 flex flex-wrap items-center gap-2 text-[11px]" data-testid="autotrade-slot-fix">
                            <span className="text-slate-400">Bakiyeye göre {slots} slot için öneri: slot başı <b className="text-slate-100">{suggest}$</b> (bot parası {Number(a.equity).toFixed(0)}$ ÷ {slots}; boş slota şimdi sığacak şekilde)</span>
                            <button type="button" disabled={busy} onClick={() => onFix(Number(settings.budget_usd || 0) >= suggest * slots ? { per_slot_usd: suggest, auto_size: false } : { per_slot_usd: suggest, budget_usd: suggest * slots, auto_size: false })}
                                    className="px-2 h-6 rounded bg-amber-500 text-slate-950 font-bold hover:bg-amber-400 disabled:opacity-50" data-testid="autotrade-slot-fix-btn">
                                Slot başı teminatı {suggest}$ yap
                            </button>
                        </div>
                    )}
                    {a.pump_open >= a.pump_max && (
                        <Line warn testId="autotrade-slot-pump">
                            Patlama slotları dolu ({a.pump_open}/{a.pump_max}): yeni 💥 PATLAMA / 🔥 ISINIYOR açılmaz, boş slot AÇ / EMİR KOY / HABER'e açık (ayar: "en fazla patlama pozisyonu")
                        </Line>
                    )}
                    {a.news_open >= a.news_max && a.news_max > 0 && <Line testId="autotrade-slot-news">Haber slotu dolu ({a.news_open}/{a.news_max}) — yeni 📰 HABER açılmaz</Line>}
                    {paused.length > 0 && (
                        <Line warn testId="autotrade-slot-paused">
                            Öğrenme: {paused.map((p) => `${p.label} ${Math.round(p.hours_left)} sa daha durduruldu`).join(' · ')} — bu türden sinyal gelse de açılmaz (boyut değişmedi)
                        </Line>
                    )}
                    {limitHit && <Line warn testId="autotrade-slot-limit">Günlük zarar limiti doldu — bugün yeni işlem açılmaz</Line>}
                    {reasons.length > 0 ? (
                        <div className="space-y-0.5" data-testid="autotrade-slot-reasons">
                            <div className="text-[10px] text-slate-500">Son taramada açılmayan adaylar ve nedenleri:</div>
                            {reasons.map(([reason, syms]) => (
                                <div key={reason} className="text-[11px] text-slate-300 pl-5">
                                    <span className="font-mono text-slate-100">{syms.slice(0, 6).join(', ')}{syms.length > 6 ? ` +${syms.length - 6}` : ''}</span>
                                    <span className="text-slate-500"> → {reason}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <Line testId="autotrade-slot-nocands">
                            Şu an açılacak uygun sinyal yok — tarama {Object.entries(settings.open_on || {}).filter(([, v]) => v !== false).map(([k]) => KIND_TR[k] || k).join(' · ') || 'seçili türlerde'} adayı üretince boş slot kullanılır
                        </Line>
                    )}
                </div>
            )}
            {free === 0 && (
                <Line ok testId="autotrade-slot-full">
                    Tüm slotlar kullanımda
                    {(view.pending || []).length > 0 && (
                        <> — bekleyen koşullu emir{(view.pending || []).length > 1 ? 'ler' : ''} slot tutuyor: {(view.pending || []).map((p) => `${p.symbol.replace('USDT', '')} (${Number(p.hours_left || 0).toFixed(1)} sa kaldı)`).join(', ')}; tetik gelmezse süresi dolunca iptal olur{settings.ac_preempt_pending !== false ? ', güçlü AÇ/PATLAMA gelirse hemen yerini bırakır' : ''}</>
                    )}
                    {(view.pending || []).length === 0 && '; bir pozisyon kapanınca sıradaki sinyal açılır'}
                </Line>
            )}
        </div>
    );
}
